import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ShieldCheck, FlaskConical, Snowflake, Syringe, HardHat, QrCode, AlertTriangle,
    Beaker, Microscope, FileCheck2, Scale, ArrowDown,
} from "lucide-react";

const SECTIONS = [
    { id: "scope", num: "01", label: "SCOPE" },
    { id: "legal", num: "02", label: "LEGAL" },
    { id: "applications", num: "03", label: "APPLICATIONS" },
    { id: "protocol", num: "04", label: "PROTOCOL" },
    { id: "quality", num: "05", label: "QUALITY" },
    { id: "compliance", num: "06", label: "COMPLIANCE" },
];

function SectionHeader({ num, label, title, subtitle }) {
    return (
        <div className="grid md:grid-cols-12 gap-6 md:gap-12 items-end mb-12">
            <div className="md:col-span-3">
                <p className="font-mono text-[10px] tracking-[0.3em] text-neon">/ {num} · {label}</p>
                <p className="font-display text-[120px] sm:text-[160px] leading-none font-bold text-ink/[0.07] mt-2 select-none" aria-hidden="true">{num}</p>
            </div>
            <div className="md:col-span-9">
                <h2 className="font-display text-3xl sm:text-5xl font-bold text-ink tracking-tight leading-[0.98]">{title}</h2>
                {subtitle && <p className="mt-4 text-base text-ink-dim leading-relaxed max-w-2xl">{subtitle}</p>}
            </div>
        </div>
    );
}

export default function ResearchGuidelines() {
    const [active, setActive] = useState("scope");

    // Highlight nav as user scrolls through sections
    useEffect(() => {
        const obs = new IntersectionObserver((entries) => {
            entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
        }, { rootMargin: "-40% 0px -55% 0px", threshold: 0 });
        SECTIONS.forEach((s) => {
            const el = document.getElementById(s.id);
            if (el) obs.observe(el);
        });
        return () => obs.disconnect();
    }, []);

    return (
        <div className="bg-ink-base text-ink min-h-screen" data-testid="research-guidelines">
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-ink">
                <div className="absolute inset-0 grid-lines opacity-30" />
                <div className="absolute inset-0 hero-vignette" />
                <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-neon/[0.06] blur-3xl pointer-events-none" />
                <div className="absolute top-32 -left-20 w-[300px] h-[300px] rounded-full bg-gold/[0.05] blur-3xl pointer-events-none" />

                <div className="relative max-w-[1280px] mx-auto px-6 sm:px-8 md:px-12 py-20 sm:py-28">
                    <div className="flex items-center gap-4 mb-8">
                        <span className="h-px w-16 bg-neon" />
                        <p className="font-mono text-[10px] tracking-[0.3em] text-neon">HELIXORA · COMPLIANCE DOCUMENT · REV 04.2026</p>
                    </div>
                    <h1 className="font-display text-5xl sm:text-7xl lg:text-[96px] font-bold tracking-tight leading-[0.92]">
                        Research<br />
                        <span className="text-neon">Guidelines.</span>
                    </h1>
                    <p className="mt-8 text-base sm:text-lg text-ink-dim leading-relaxed max-w-2xl">
                        Everything our compounds are — and aren't — for. A short, plain-language reference for the labs and researchers we ship to.
                    </p>

                    <div className="mt-12 flex items-center gap-2 text-ink-mute font-mono text-[10px] tracking-wider">
                        <ArrowDown className="w-3 h-3" /> SCROLL · 6 SECTIONS · ~4 MIN READ
                    </div>
                </div>
            </section>

            {/* CORE DISCLAIMER STRIP */}
            <section className="border-b border-ink bg-gold/[0.04]">
                <div className="max-w-[1280px] mx-auto px-6 sm:px-8 md:px-12 py-8">
                    <div className="flex items-start gap-5 max-w-4xl">
                        <AlertTriangle className="w-8 h-8 text-gold shrink-0 mt-1" strokeWidth={1.4} />
                        <p className="text-base sm:text-lg text-ink leading-relaxed">
                            All Helixora compounds are <span className="text-gold font-semibold">research chemicals</span> — sold strictly for in-vitro and lab use by qualified researchers. Not a medical product. Not a supplement. Not for human or animal consumption.
                        </p>
                    </div>
                </div>
            </section>

            {/* MAIN BODY · two-column with sticky TOC */}
            <div className="max-w-[1280px] mx-auto px-6 sm:px-8 md:px-12 py-20 grid md:grid-cols-[200px_1fr] gap-12 lg:gap-20">
                {/* TOC */}
                <aside className="hidden md:block">
                    <div className="sticky top-32" data-testid="rg-toc">
                        <p className="font-mono text-[10px] tracking-[0.3em] text-ink-mute mb-6">/ TABLE OF CONTENTS</p>
                        <nav className="space-y-1">
                            {SECTIONS.map((s) => (
                                <a key={s.id} href={`#${s.id}`}
                                    className={`block py-2.5 pl-3 border-l-2 transition-all duration-200 font-mono text-[11px] tracking-wider ${active === s.id ? "border-neon text-neon" : "border-ink-strong text-ink-mute hover:text-ink-dim"}`}
                                    data-testid={`rg-toc-${s.id}`}>
                                    {s.num} · {s.label}
                                </a>
                            ))}
                        </nav>
                    </div>
                </aside>

                {/* CONTENT */}
                <div className="space-y-32">
                    {/* SCOPE */}
                    <section id="scope" data-testid="rg-scope">
                        <SectionHeader num="01" label="SCOPE" title="Who our products are for."
                            subtitle="Helixora supplies high-purity research peptides exclusively to qualified researchers, university labs, and institutional studies. Every vial is produced, tested, and distributed under a strict research-only mandate." />

                        <div className="grid sm:grid-cols-3 gap-px bg-ink-strong">
                            {[
                                { n: "01", t: "Research-grade only", s: "Synthesised under cGMP-aligned protocols. >98% peptide purity verified per batch." },
                                { n: "02", t: "Independently tested", s: "Janoshik Analytical 3rd-party HPLC + mass spec on every batch — never self-reported." },
                                { n: "03", t: "Lab-only audience", s: "Sold to researchers, academic labs, and institutions. Not retail. Not consumer goods." },
                            ].map((c) => (
                                <div key={c.n} className="bg-ink-base p-7">
                                    <span className="font-mono text-[10px] text-neon tracking-wider">{c.n}</span>
                                    <h3 className="mt-3 font-display text-xl font-semibold text-ink leading-tight">{c.t}</h3>
                                    <p className="mt-3 text-sm text-ink-dim leading-relaxed">{c.s}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* LEGAL */}
                    <section id="legal" data-testid="rg-legal">
                        <SectionHeader num="02" label="LEGAL" title="The fine print, in plain English." />

                        <div className="border border-gold/30 bg-gold/[0.04] p-8">
                            <Scale className="w-6 h-6 text-gold mb-5" strokeWidth={1.5} />
                            <p className="text-ink leading-relaxed">
                                All products sold by Helixora Peptides are <span className="text-gold font-semibold">strictly for research and laboratory use</span>. They are <span className="text-gold font-semibold">not intended for human consumption, diagnostic, therapeutic, cosmetic, or any non-research purpose</span>.
                            </p>
                            <p className="mt-5 text-ink-dim leading-relaxed">
                                By placing an order, you confirm you are a qualified researcher or institution conducting legitimate scientific research, and you accept responsibility for compliance with all applicable local, provincial, state, and federal regulations.
                            </p>
                        </div>
                    </section>

                    {/* APPLICATIONS */}
                    <section id="applications" data-testid="rg-applications">
                        <SectionHeader num="03" label="APPLICATIONS" title="Intended uses." subtitle="A non-exhaustive list of research contexts our compounds support." />

                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink-strong">
                            {[
                                { icon: Beaker, t: "In-vitro cell culture", s: "Receptor binding, signaling assays, viability studies." },
                                { icon: Microscope, t: "Biochemical assays", s: "ELISA, western blot, kinase activity, peptide-protein interaction." },
                                { icon: FlaskConical, t: "Animal model research", s: "IACUC-approved pharmacokinetic and efficacy studies in qualified protocols." },
                                { icon: Syringe, t: "Method development", s: "HPLC, LC-MS, and analytical method validation." },
                                { icon: ShieldCheck, t: "SAR studies", s: "Structure-activity relationship work, peptide modification research." },
                                { icon: FileCheck2, t: "Reference standards", s: "Internal QA standards for laboratories and institutional analytics." },
                            ].map((a, i) => (
                                <div key={i} className="bg-ink-base p-7">
                                    <a.icon className="w-5 h-5 text-neon mb-4" strokeWidth={1.5} />
                                    <h3 className="font-display text-base font-semibold text-ink">{a.t}</h3>
                                    <p className="mt-2 text-sm text-ink-dim leading-relaxed">{a.s}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* PROTOCOL */}
                    <section id="protocol" data-testid="rg-protocol">
                        <SectionHeader num="04" label="PROTOCOL" title="Best-practice handling." subtitle="Reconstitution, storage, and lab safety guidance for our peptides." />

                        <div className="space-y-px bg-ink-strong">
                            <div className="grid md:grid-cols-[40px_1fr_1fr] gap-px bg-ink-strong">
                                {[
                                    {
                                        n: "A", icon: FlaskConical, t: "Reconstitution",
                                        bullets: ["Use sterile bacteriostatic water (BAC) or sterile saline (0.9% NaCl).", "Add solvent slowly down the inside wall of the vial.", "Gently swirl — never shake. Vortex denatures peptide bonds."],
                                    },
                                    {
                                        n: "B", icon: Snowflake, t: "Storage",
                                        bullets: ["Lyophilized vials: 2–8°C (refrigerator), shielded from direct light.", "Reconstituted solution: 2–8°C, use within 14–28 days.", "Long-term lyophilized storage: −20°C, away from frost-free defrost cycles."],
                                    },
                                    {
                                        n: "C", icon: Syringe, t: "Dosing measurement (lab use)",
                                        bullets: ["Use calibrated micropipettes or insulin-grade syringes for precision.", "Document each draw — concentration, volume, batch ID.", "Discard partial draws. Never re-aspirate from a working solution."],
                                    },
                                    {
                                        n: "D", icon: HardHat, t: "Safety & compliance",
                                        bullets: ["PPE: gloves, eye protection, lab coat. Work in certified BSC where required.", "Follow institutional SOPs for chemical handling, waste, and sharps.", "Maintain chain-of-custody records for IRB / IACUC audits."],
                                    },
                                ].map((p) => (
                                    <React.Fragment key={p.n}>
                                        <div className="bg-ink-base p-6 flex items-center justify-center">
                                            <span className="font-display text-3xl font-bold text-neon">{p.n}</span>
                                        </div>
                                        <div className="bg-ink-base p-6 md:col-span-2 sm:flex gap-6 items-start">
                                            <p.icon className="w-5 h-5 text-neon mb-3 sm:mb-0 sm:mt-1 shrink-0" strokeWidth={1.5} />
                                            <div>
                                                <h3 className="font-display text-lg font-semibold text-ink">{p.t}</h3>
                                                <ul className="mt-3 space-y-2 text-sm text-ink-dim leading-relaxed">
                                                    {p.bullets.map((b, i) => (
                                                        <li key={i} className="flex gap-3"><span className="text-neon font-mono shrink-0">·</span><span>{b}</span></li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* QUALITY */}
                    <section id="quality" data-testid="rg-quality">
                        <SectionHeader num="05" label="QUALITY" title="Independently verified." subtitle="Every batch tested by Janoshik Analytical — full COA per lot, linked from the product page." />

                        <div className="border border-ink-strong p-8 sm:p-12 bg-ink-surface">
                            <div className="grid sm:grid-cols-3 gap-8">
                                {[
                                    { v: ">98%", l: "PEPTIDE PURITY (HPLC)" },
                                    { v: "100%", l: "BATCHES TESTED" },
                                    { v: "3RD PARTY", l: "JANOSHIK ANALYTICAL" },
                                ].map((s) => (
                                    <div key={s.l}>
                                        <p className="font-display text-4xl sm:text-5xl font-bold text-neon">{s.v}</p>
                                        <p className="mt-2 font-mono text-[10px] tracking-[0.2em] text-ink-mute">{s.l}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-8 pt-8 border-t border-ink flex flex-wrap gap-3">
                                <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-neon/30 bg-neon/5 font-mono text-[10px] uppercase tracking-wider text-neon">
                                    <ShieldCheck className="w-3.5 h-3.5" /> JANOSHIK COA VERIFIED
                                </span>
                                <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-ink-strong font-mono text-[10px] uppercase tracking-wider text-ink-dim">
                                    <QrCode className="w-3.5 h-3.5 text-neon" /> QR-LINKED PER BATCH
                                </span>
                                <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-ink-strong font-mono text-[10px] uppercase tracking-wider text-ink-dim">
                                    <FlaskConical className="w-3.5 h-3.5 text-neon" /> HPLC + MASS SPEC
                                </span>
                            </div>
                        </div>
                    </section>

                    {/* COMPLIANCE */}
                    <section id="compliance" data-testid="rg-compliance">
                        <SectionHeader num="06" label="COMPLIANCE" title="The bottom line." />

                        <div className="border-l-2 border-neon pl-8 py-2 mb-10">
                            <p className="text-lg sm:text-xl text-ink leading-relaxed">
                                Helixora Peptides is committed to supporting <span className="text-neon font-semibold">legitimate scientific research</span>. All customers must comply with local, provincial, state, and federal regulations regarding research chemicals.
                            </p>
                        </div>

                        <div className="flex flex-wrap gap-3 pt-2">
                            <Link to="/shop" className="bg-neon hover:bg-neon-glow text-black px-7 py-3.5 font-bold uppercase tracking-wider text-xs transition-colors" data-testid="rg-back-to-shop">
                                Back to catalog →
                            </Link>
                            <Link to="/" className="border border-ink-strong hover:border-neon text-ink-dim hover:text-ink px-7 py-3.5 font-bold uppercase tracking-wider text-xs transition-colors">
                                Homepage
                            </Link>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}
